import React, { useEffect, useState } from 'react';

import Icon from '../common/Icon';
import { Select } from '@copilot/ui';
import CreateCompanyModal from './CreateCompanyModal';
import * as companyApi from '../../services/companyApi';

const CREATE_NEW_VALUE = '__create_new__';

// Selected company's "AI context available" preview - informational
// counts only (backend/routers/companies.py's context-preview
// endpoint), never the memory/action-item content itself.
function ContextPreview({ companyId, companyName }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setPreview(null);
    setError('');

    companyApi
      .getCompanyContextPreview(companyId)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Unable to load related context.');
      });

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return (
    <div className="company-context-preview">
      <div className="company-context-preview-header">
        <span className="company-context-preview-icon" aria-hidden="true">
          <Icon name="briefcase" size={16} />
        </span>
        <div>
          <span>Related To:</span>
          <strong>{companyName}</strong>
        </div>
      </div>

      {error && <p className="field-help">{error}</p>}

      {!error && !preview && <p className="field-help">Loading related context...</p>}

      {preview && (
        <ul className="company-context-preview-list">
          <li>
            <Icon name="check-circle" size={14} />
            <span>Previous Conversations: {preview.previous_meetings_count}</span>
          </li>
          <li>
            <Icon name="check-circle" size={14} />
            <span>Relevant Memories: {preview.memory_count}</span>
          </li>
          <li>
            <Icon name="check-circle" size={14} />
            <span>Previous Action Items: {preview.pending_items_count}</span>
          </li>
        </ul>
      )}
    </div>
  );
}

function CompanySelector({ value, onChange }) {
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    companyApi
      .getCompanies()
      .then((data) => {
        if (!cancelled) setCompanies(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Unable to load companies.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelectChange = (event) => {
    const nextValue = event.target.value;

    if (nextValue === CREATE_NEW_VALUE) {
      setIsModalOpen(true);
      return;
    }

    onChange(nextValue || '');
  };

  const handleCreated = (company) => {
    setCompanies((current) => [...current, company]);
    setIsModalOpen(false);
    onChange(company.id);
  };

  const selectedCompany = companies.find((c) => c.id === value);

  return (
    <div className="company-selector">
      {isLoading ? (
        <p className="field-help">Loading related context...</p>
      ) : error ? (
        <p className="field-help">Unable to load related context.</p>
      ) : (
        <>
          <Select
            id="meeting-company"
            value={value || ''}
            onChange={handleSelectChange}
          >
            <option value="">No related context</option>
            {companies.length === 0 && (
              <option value="" disabled>
                No related context yet. Create one.
              </option>
            )}
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
            <option value={CREATE_NEW_VALUE}>+ Create New Related Context</option>
          </Select>

          {selectedCompany && (
            <ContextPreview companyId={selectedCompany.id} companyName={selectedCompany.name} />
          )}
        </>
      )}

      {isModalOpen && (
        <CreateCompanyModal
          onClose={() => setIsModalOpen(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}

export default CompanySelector;
